import type { FC } from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
  DialogForm,
  StandardTable,
  TableSkeleton,
  InstantSearch,
  Button,
  HelpLabel,
  StoryblokHelpBlock,
} from "@components";
import { HiTrash, HiPencilAlt } from "react-icons/hi";

import { Driver } from "../../models";
import { useCache } from "../../context/CacheContext";
import { DriverAvatar } from "./DriverAvatar";

interface DriversListProps {
  drivers: Driver[];
  isLoading: boolean;
  createOrUpdate: (formData: Driver) => void;
}

export const DriversList: FC<DriversListProps> = ({
  drivers,
  isLoading,
  createOrUpdate,
}) => {
  const { t } = useTranslation(["common", "driver"]);
  const navigate = useNavigate();
  const { updateCacheKey } = useCache();

  const [driverData, setDriverData] = useState<Driver>(Driver.default());
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);

  const [searchText, setSearchText] = useState("");
  const [filteredDrivers, setFilteredDrivers] = useState<Driver[]>(drivers);

  const openDriver = (driver: Driver) => {
    if (driver.id)
      navigate(`/resources/drivers/${encodeURIComponent(driver.id)}`);
  };

  // Filter drivers based on search text
  useEffect(() => {
    if (searchText.trim().length > 0) {
      const lowerCaseSearchText = searchText.toLowerCase();
      setFilteredDrivers(
        drivers.filter((driver) =>
          (driver.firstName + " " + driver.lastName)
            .toLowerCase()
            .includes(lowerCaseSearchText),
        ),
      );
    } else {
      setFilteredDrivers(drivers);
    }
  }, [searchText, drivers]);

  const showDutyStatus = drivers.some((driver) => driver.onDuty !== undefined);
  const showTrackingStatus = drivers.some(
    (driver) => driver.trackingOn !== undefined,
  );

  const headers = [
    { id: "name", children: t("common:drivers"), showOnSmallScreen: true },
    ...(showDutyStatus
      ? [
          {
            id: "duty",
            children: t("driver:status.duty"),
            showOnSmallScreen: true,
          },
        ]
      : []),
    ...(showTrackingStatus
      ? [
          {
            id: "tracking",
            children: t("driver:status.tracking"),
            showOnSmallScreen: false,
          },
        ]
      : []),
    {
      id: "email",
      children: t("driver:email"),
      showOnSmallScreen: false,
    },
  ];

  const statusBadge = (
    active: boolean | undefined,
    activeText: string,
    inactiveText: string,
  ) => (
    <span className="inline-flex items-center gap-2 whitespace-nowrap text-sm">
      <span
        className={`h-2 w-2 rounded-full ${active === true ? "bg-lgb-green-300" : "bg-gray-300 dark:bg-gray-600"}`}
      />
      {active === undefined ? "—" : active ? activeText : inactiveText}
    </span>
  );

  // Table rows
  const tableRows = filteredDrivers.map((driver, index) => ({
    // Legacy API responses may omit the embedded Firestore id. Email is
    // unique within a workspace and keeps row identity stable until the API
    // hydration fix is deployed.
    id: driver.id || `${driver.email || "legacy-driver"}-${index}`,
    onRowClickData: driver,
    cells: [
      {
        id: "name",
        children: (
          <span className="flex min-w-0 items-center gap-2">
            <DriverAvatar
              driverId={driver.id}
              workspaceId={driver.workspaceId}
              name={Driver.getDriverName(driver, true)}
            />
            <span className="truncate">
              {Driver.getDriverName(driver, true) || t("driver:unknown_driver")}
            </span>
          </span>
        ),
        showOnSmallScreen: true,
      },
      ...(showDutyStatus
        ? [
            {
              id: "duty",
              children: statusBadge(
                driver.onDuty,
                t("driver:status.on_duty"),
                t("driver:status.off_duty"),
              ),
              showOnSmallScreen: true,
            },
          ]
        : []),
      ...(showTrackingStatus
        ? [
            {
              id: "tracking",
              children: statusBadge(
                driver.trackingOn,
                t("driver:status.tracking_on"),
                t("driver:status.tracking_off"),
              ),
              showOnSmallScreen: false,
            },
          ]
        : []),
      {
        id: "email",
        children: driver.email || "",
        showOnSmallScreen: false,
      },
    ],
    actions: [
      {
        id: "edit",
        icon: <HiPencilAlt />,
        onActionClick: (e: React.MouseEvent) => {
          e.stopPropagation();
          openDriver(driver);
        },
      },
      {
        id: "delete",
        icon: <HiTrash />,
        color: "failure",
        onActionClick: (e: React.MouseEvent) => {
          e.stopPropagation();
          setDriverData(driver);
          setDeleteModalOpen(true);
        },
      },
    ],
  }));

  // Handle vehicle deletion
  const handleDelete = async () => {
    try {
      await Driver.remove(driverData);
      setDeleteModalOpen(false);
      updateCacheKey();
    } catch (error) {
      console.error("Error deleting driver:", error);
    }
  };

  return (
    <div className="overflow-hidden">
      {/* Header */}
      <div className="flex flex-col justify-between items-start gap-4 mb-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-semibold text-text sm:text-2xl">
              {t("driver:title")}
            </h1>
            <HelpLabel
              content={
                <StoryblokHelpBlock
                  storySlug="logibud-tms-web/help-popups/drivers"
                  accessToken="Jf9NMAu4DXeo3fwdjd1w7wtt"
                />
              }
              className="mt-1"
            />
          </div>
          <p className="text-secondary text-sm sm:text-base pt-1 sm:pt-2 pb-2 sm:pb-4 dark:text-secondary">
            {t("driver:description")}
          </p>
        </div>

        {/* Filter bar */}
        <div className="flex flex-col md:flex-row gap-3 mb-4 w-full">
          {/* Driver search */}
          <div className="flex-grow relative">
            <InstantSearch
              placeholder={t("driver:search_placeholder", {
                defaultValue: "Søk etter sjåfør...",
              })}
              value={searchText}
              onChange={setSearchText}
            />
          </div>

          <div className="flex items-center">
            <Button
              variant="add"
              onClick={() => {
                createOrUpdate(Driver.default());
              }}
              size="md"
            >
              {t("driver:new_driver")}
            </Button>
          </div>
        </div>
      </div>

      {isLoading ? (
        <TableSkeleton />
      ) : (
        <StandardTable
          hasActions
          rows={tableRows}
          header={headers}
          onRowClick={openDriver}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <DialogForm
        title={`${t("driver:actions.delete_dialogTitle")} ${
          driverData.firstName + " " + driverData.lastName ||
          t("driver:unknown_driver")
        }?`}
        dismissible
        show={isDeleteModalOpen}
        toggleModal={() => setDeleteModalOpen(false)}
        buttonConfirmColor="failure"
        confirmButton={handleDelete}
        buttonConfirmText={t("common:confirm")}
        buttonCloseText={t("common:close")}
      />
    </div>
  );
};
